/**
 * =============================================================================
 * Session persistence helpers (FE issue #24)
 * =============================================================================
 *
 * Why this exists
 * ---------------
 * `keycloak.init()` was called without `onLoad`, so keycloak-js never tried to
 * restore anything after a page reload: `keycloak.authenticated` came back
 * `false`, the first API call had no bearer and the user was dropped on the
 * login screen. Hitting F5 looked exactly like being logged out.
 *
 * Two independent recovery paths are used now, in this order:
 *  1. the tokens this tab stored on its last successful auth (fast, works even
 *     when third-party cookies are blocked — keycloak-js refreshes them on
 *     init and drops them if the refresh token is dead),
 *  2. a silent `check-sso` iframe against Keycloak (picks up a session started
 *     in another tab / after the tab was closed).
 *
 * Tokens live in **sessionStorage**: they survive a reload of this tab, which
 * is what the user complained about, but they are gone when the tab is closed,
 * so a shared workstation does not keep an admin bearer lying around.
 *
 * The "session expired" flag lives in localStorage on purpose — it has to
 * survive the full page load that follows a hard redirect so the login page can
 * explain what happened instead of silently appearing.
 */

const TOKENS_KEY = 'rehapiano_kc_tokens'
const EXPIRED_KEY = 'rehapiano_session_expired'

export interface StoredKeycloakTokens {
  token: string
  refreshToken: string
  idToken?: string
}

/** Reason the session ended — drives which message the login page shows. */
export type SessionEndReason = 'expired' | 'restore_failed'

/** Custom DOM event the API layer fires when a request comes back 401. */
export const SESSION_EXPIRED_EVENT = 'rehapiano:session-expired'

export const readKeycloakTokens = (): StoredKeycloakTokens | null => {
  try {
    const raw = window.sessionStorage.getItem(TOKENS_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as StoredKeycloakTokens
    if (!parsed?.token || !parsed?.refreshToken) {
      window.sessionStorage.removeItem(TOKENS_KEY)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

export const saveKeycloakTokens = (tokens: Partial<StoredKeycloakTokens>): void => {
  if (!tokens.token || !tokens.refreshToken) return
  try {
    window.sessionStorage.setItem(
      TOKENS_KEY,
      JSON.stringify({
        token: tokens.token,
        refreshToken: tokens.refreshToken,
        idToken: tokens.idToken,
      }),
    )
  } catch {
    // storage unavailable (private mode) — silent check-sso still covers reload
  }
}

export const clearKeycloakTokens = (): void => {
  try {
    window.sessionStorage.removeItem(TOKENS_KEY)
  } catch {
    // ignore
  }
}

/**
 * Remember that an authenticated session ended by itself, so the login screen
 * can say so. Never called on an explicit sign-out.
 */
export const markSessionEnded = (reason: SessionEndReason = 'expired'): void => {
  try {
    window.localStorage.setItem(EXPIRED_KEY, reason)
  } catch {
    // ignore
  }
}

/** Read and clear the flag — the message is shown exactly once. */
export const consumeSessionEnded = (): SessionEndReason | null => {
  try {
    const value = window.localStorage.getItem(EXPIRED_KEY)
    if (value) window.localStorage.removeItem(EXPIRED_KEY)
    return value === 'expired' || value === 'restore_failed' ? value : null
  } catch {
    return null
  }
}

export const clearSessionEnded = (): void => {
  try {
    window.localStorage.removeItem(EXPIRED_KEY)
  } catch {
    // ignore
  }
}

/**
 * Tell the app that the current session is gone. Dispatched instead of the old
 * `window.location.href = '/'`, which reloaded the SPA and threw away any
 * chance of explaining what happened.
 */
export const notifySessionExpired = (reason: SessionEndReason = 'expired'): void => {
  markSessionEnded(reason)
  try {
    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT, { detail: reason }))
  } catch {
    // ignore
  }
}
